/**
 * Device identity capture for web. Uses ThumbmarkJS to produce two
 * per-browser hashes and posts them to POST /v1/device-identify so the
 * backend can persist them alongside the server-computed JA4 + Client
 * Hints fingerprint. Log-only — the backend uses these signals for
 * abuse evaluation, not for blocking.
 *
 * Runs at most once per browser session (sessionStorage gate). The
 * backend endpoint is idempotent, so the gate is a pure cost-saving
 * measure.
 *
 * Privacy: the resulting hashes are first-party, scoped to this app,
 * and used only for abuse prevention. Disclosed in the privacy policy
 * under "Device identifiers and abuse prevention".
 */

import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';
import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';
import { isNetworkErrorMessage } from './network-errors.js';

const SESSION_GATE_KEY_PREFIX = 'ip/device-identified:';

/**
 * Read the `uid` claim from a JWT without verifying its signature.
 * We're not authenticating — the server already does that on the POST —
 * we just need a per-account key so a tab switched from user A to
 * user B posts B's hashes too.
 *
 * Returns the string 'anon' on any parse failure (missing token,
 * non-JWT shape, unparseable payload) — the server-side auth check
 * catches everything that matters.
 */
function currentUserIdFromToken(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return 'anon';
        }
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return String(payload.uid ?? 'anon');
    } catch (_) {
        return 'anon';
    }
}

/**
 * Build a ThumbmarkJS instance with the components we want, excluding
 * the three that drift on each browser update (canvas, audio,
 * permissions) — those go into the "full" hash only, not the stable
 * one that becomes user_devices.device_id for web.
 */
function buildStableThumbmark() {
    return new Thumbmark({
        exclude: ['canvas', 'audio', 'permissions'],
        logging: false,
    });
}

function buildFullThumbmark() {
    return new Thumbmark({ logging: false });
}

/**
 * Fire-and-forget: compute both hashes in parallel and POST once.
 * Errors are swallowed — Phase 1b is log-only, and a broken fingerprint
 * POST must never affect the user's session.
 */
async function identifyDevice() {
    try {
        // Authenticated-only: no token, no point. The /v1/device-identify
        // endpoint is a PrivateController (401s without X-AUTH-TOKEN).
        // Must await the async helper because token-initializer races us
        // on the first post-login page load — a sync localStorage read
        // would miss the most valuable capture moment.
        const token = await getApiToken();
        if (!token) {
            return;
        }

        // Key the session gate by user id so logout + login-as-other
        // in the same tab still captures the second user's identity.
        // user_devices is keyed by (user_id, device_id) on the server;
        // without per-user gating the second user would never get a row.
        const gateKey = SESSION_GATE_KEY_PREFIX + currentUserIdFromToken(token);
        if (sessionStorage.getItem(gateKey) === '1') {
            return;
        }

        const stableMarker = buildStableThumbmark();
        const fullMarker = buildFullThumbmark();

        const [stableResult, fullResult] = await Promise.all([
            stableMarker.get(),
            fullMarker.get(),
        ]);

        const body = new URLSearchParams({
            stable: stableResult.thumbmark,
            full: fullResult.thumbmark,
        });

        const response = await apiClient.request('/device-identify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });

        if (response.ok) {
            sessionStorage.setItem(gateKey, '1');
        }
    } catch (err) {
        // Deliberate: any failure here is invisible to the user.
        // Network failures (offline, flaky connection) are expected and
        // not reported. The sessionStorage gate stays unset on failure,
        // so the next page load retries.
        if (err instanceof TypeError && isNetworkErrorMessage(err.message)) {
            return;
        }
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.captureException(err);
        }
    }
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', identifyDevice);
    } else {
        identifyDevice();
    }
}
